/*  src/pages/WelcomePage.tsx  */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import AuthCard from '../components/AuthCard.tsx';
import AreaChoiceCard from '../components/AreaChoiceCard.tsx';
import {
  applySignedInRoute,
  routeAfterSignIn,
  type AreaChoice,
} from '../utils/routeAfterSignIn.ts';
import { waitForAuthSession } from '../utils/waitForAuthSession.ts';

function oauthErrorFromUrl(): string | null {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const description =
    search.get('error_description') ||
    hash.get('error_description') ||
    search.get('error') ||
    hash.get('error');
  if (!description) return null;
  if (description.toLowerCase().includes('identity')) {
    return 'An account with that email already exists. Log in with your password instead.';
  }
  return description.replace(/\+/g, ' ');
}

/**
 * Where email confirmation and Google OAuth both land. Supabase establishes
 * the session from the URL, then we either provision an email signup, send a
 * new Google user to finish year/subjects, or route an existing account the
 * same way the login form would.
 */
const WelcomePage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<AreaChoice | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fromProvider = oauthErrorFromUrl();
    if (fromProvider) {
      setError(fromProvider);
      return;
    }

    (async () => {
      const session = await waitForAuthSession();
      if (!session?.user) {
        setError('That sign-in link has expired. Try logging in instead.');
        return;
      }

      const result = await routeAfterSignIn(session.user, { allowCompleteSignup: true });
      applySignedInRoute(result, session, { navigate, setChoice, setError });
    })();
  }, [navigate]);

  if (choice) {
    return <AreaChoiceCard choice={choice} navigate={navigate} />;
  }

  if (error) {
    return (
      <AuthCard title="Something went wrong">
        <Alert severity="error">{error}</Alert>
        <Button component={RouterLink} to="/login" variant="outlined">
          Go to login
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Setting up your account…">
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress />
      </Box>
    </AuthCard>
  );
};

export default WelcomePage;
